import { lazy, Suspense, useCallback, useState, type ReactNode } from 'react';

import {
  useAssistantPanelState,
  type AssistantPanelState,
} from '@/features/assistant/AssistantLayout.js';
import { fileReferenceKey, type FileReference } from '@/lib/api/file-chat.js';
import { agentGuideEntryUrl } from '@/lib/guide-links.js';

import type { UndoableEdit } from './file-edit.js';
import { useFileThread, type FileThread } from './useFileThread.js';

/**
 * The panel and the chat machinery behind it, loaded only once a file's Assistant is there. The Files
 * page is in the app shell, and a static import would pull the whole chat UI into it.
 */
const FileAssistantPanel = lazy(async () => ({
  default: (await import('./FileAssistantPanel.js')).FileAssistantPanel,
}));

/**
 * The file viewers' own keys for the Assistant's width and collapse. The panel starts collapsed:
 * people open a file to look at it, and the Assistant opens on demand.
 */
const FILE_ASSISTANT_KEYS = {
  width: 'cti.files.chatWidth',
  collapsed: 'cti.files.chatCollapsed',
};

/**
 * Shown in place of the Assistant's composer while an older version of the file is on screen: the
 * Assistant edits the current file, not the one shown.
 */
export const OLDER_REVISION_TEXT =
  'Go back to the latest version to edit it with the assistant.';

/**
 * Shown in place of the Assistant's composer while the reader edits the file by hand: an Assistant
 * edit would move the file under them and their Save would conflict.
 */
export const EDITING_TEXT = 'Finish editing to use the assistant.';

/** The guide entry the viewer's question mark opens while the Assistant is there. */
export function fileAssistantHelpHref(): string {
  return agentGuideEntryUrl('edit-files-with-the-assistant');
}

/** What the file viewer's shell needs to lay the Assistant beside the preview. */
export interface ViewerAssistant {
  panel: AssistantPanelState;
  chat: (collapse: () => void) => ReactNode;
}

/** The Assistant of one open file, as a viewer uses it. */
export interface FileAssistant {
  /** True when this file gets an Assistant here. */
  available: boolean;
  thread: FileThread;
  panel: AssistantPanelState;
  /** The shell's assistant slot, or undefined when there is no Assistant. */
  layout: ViewerAssistant | undefined;
  /** The newest assistant edit that can be undone, or null. */
  undoableEdit: UndoableEdit | null;
  /** Drops an undone edit at once, before the chat's next poll says so. */
  markUndone: (edit: UndoableEdit) => void;
  /** True while a turn runs in the file's chat, which may change the file any moment. */
  busy: boolean;
}

/**
 * The Assistant beside a file viewer: finds the file's edit chat, keeps the panel's width and
 * collapse, and carries the newest undoable edit up to the viewer's toolbar. `enabled` is false for
 * a file with no preview, which gets no Assistant at all.
 */
export function useFileAssistant({
  workspaceId,
  file,
  fileName,
  enabled,
  composerBlocked = null,
}: {
  workspaceId: string;
  /** Memoized by the caller: a new object each render would look up the chat again. */
  file: FileReference;
  fileName: string;
  enabled: boolean;
  /** Why the reader may not send right now, such as an older version on screen; null when they may. */
  composerBlocked?: string | null;
}): FileAssistant {
  const thread = useFileThread(workspaceId, file, `File: ${fileName}`, enabled);
  const panel = useAssistantPanelState(FILE_ASSISTANT_KEYS, true);
  const [reportedEdit, setReportedEdit] = useState<UndoableEdit | null>(null);
  const [busy, setBusy] = useState(false);
  const [undoneIds, setUndoneIds] = useState<ReadonlySet<string>>(new Set());
  const available = enabled && thread.supported === true;
  const markUndone = useCallback((edit: UndoableEdit) => {
    setUndoneIds((current) => new Set(current).add(edit.messageId));
  }, []);
  const undoableEdit =
    reportedEdit && !undoneIds.has(reportedEdit.messageId)
      ? reportedEdit
      : null;
  return {
    available,
    thread,
    panel,
    layout: available
      ? {
          panel,
          chat: (collapse) => (
            <Suspense fallback={null}>
              <FileAssistantPanel
                key={fileReferenceKey(file)}
                workspaceId={workspaceId}
                file={file}
                thread={thread}
                onCollapse={collapse}
                onUndoableEditChange={setReportedEdit}
                onBusyChange={setBusy}
                composerBlocked={composerBlocked}
              />
            </Suspense>
          ),
        }
      : undefined,
    undoableEdit: available ? undoableEdit : null,
    markUndone,
    busy: available && busy,
  };
}
